import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Flight } from '@ibe/models';
import * as fromStore from '@ibe/store/index';
import * as FlightResultActions from '@ibe/store/actions/flight-result.actions';

import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';
import { INDRABookingSearchService } from '@ibe.services/booking-search.indra.service';
import { INDRABookingService } from "@ibe.services/booking.indra.service";
import { INDRADateService } from '@ibe.services/date.indra.service';
import { SidebarService } from '@ibe/services/ui';


import { EQUIPMENT_CODE_BUS, EQUIPMENT_CODE_TRAIN } from '@ibe.config';

@Component({
	selector: 'app-flight-route',
	templateUrl: './flight-route.indra.component.html',
	styleUrls: ['./flight-route.component.scss'],
	host: {
		class: 'app-flight-route'
	}
})
export class INDRAFlightRouteComponent implements OnInit, OnDestroy {

	@Input() flight;
	
	isMYB: boolean = false;
	isReviewFlights: boolean = false;
	isTimeToThinkFlights: boolean = false;
	
	constructor(protected route: ActivatedRoute,
		protected router: Router,
		private store: Store<fromStore.State>,
		protected sidebarService: SidebarService,
		private bookingSearchService: INDRABookingSearchService,
		private bookingService: INDRABookingService,
		private ibeAdminService: INDRAIbeAdminService,
		private dateService: INDRADateService) 
	{ }

	ngOnInit() {
		if (this.router.url.includes('/my-bookings/details') || this.router.url.includes('/my-bookings/view')) this.isMYB = true;
		if (this.router.url.includes('/review-your-trip') || this.router.url.includes('/payment')) this.isReviewFlights = true;
		if (this.router.url.includes('/time-to-think')) this.isTimeToThinkFlights = true;
	}

	ngOnDestroy(): void { }
	
	INDRA_getTextTranslation(key: string): string {
		return this.ibeAdminService.getTranslationValue(key);
	}
	
	private getAirlines() {
		const airlines = [];
		this.flight.listSegment.forEach(segment => {
			if (!airlines.includes(segment.operationCarrier) && 
				segment.equipment !== EQUIPMENT_CODE_BUS && segment.equipment !== EQUIPMENT_CODE_TRAIN) {
				airlines.push(segment.operationCarrier);
			}
		});
		return airlines;
	}
	
	private getFlightTripFirstSegment() {
		if (this.flight.listSegment && this.flight.listSegment.length > 0) {
			return this.flight.listSegment[0];
		}
	}
	
	private getFlightTripLastSegment() {
		if (this.flight.listSegment && this.flight.listSegment.length > 0) {
			const lastSegmentIndex = this.flight.listSegment.length - 1;
			return this.flight.listSegment[lastSegmentIndex];
		}
	}
	
	INDRA_printAirlineName() {
		let airlineName : string;
		const airlines = this.getAirlines();
		if (airlines.length > 1) {
			airlineName = 'Multiple airlines';
		} else {
			airlineName = this.isMYB || this.isReviewFlights || this.isTimeToThinkFlights ?
				this.bookingService.INDRA_getAirplaneInfo(airlines[0]).name :
				this.bookingSearchService.INDRA_getFlightSearchAirplaneInfo(airlines[0]).name;
		}
		return airlineName;
	}
	
	INDRA_returnAriaLabelTranslation() {
		return this.INDRA_getTextTranslation('bookingDetails.aria.flightOperatedBy');
	}
	
	INDRA_printAirlineLogo() {
		let airlineLogo;
		const airlines = this.getAirlines();
		if (airlines.length > 1) {
			airlineLogo = 'Multiple';
		} else {
			airlineLogo = airlines[0];
		}
		return this.bookingSearchService.INDRA_buildAirlineLogoName(airlineLogo);
	}
	
	INDRA_ariaLabelRoute() {
		let label = '';
		const firstSegment = this.getFlightTripFirstSegment();
		const lastSegment = this.getFlightTripLastSegment();
		
		if (firstSegment && firstSegment) {
			label += 'Flight from ' + firstSegment.departureAirport;
			label += ' to ' + lastSegment.arrivalAirport;
		}
		return label;
	}
	
	INDRA_getLocation(code: string) {
		return ((this.isMYB || this.isReviewFlights || this.isTimeToThinkFlights) ? 
			this.bookingService.INDRA_getLocation(code) : 
			this.bookingSearchService.INDRA_getFlightSearchLocation(code));
	}
	
	INDRA_getDepartureLocation() {
		return this.INDRA_getLocation(
			this.bookingService.INDRA_getFirstSegmentFlightIgnoringTrain(this.flight.listSegment)['departureAirport']);
	}
	
	INDRA_getArrivalLocation() {
		return this.INDRA_getLocation(
			this.bookingService.INDRA_getLastSegmentFlightIgnoringTrain(this.flight.listSegment)['arrivalAirport']);
	}
	
	INDRA_returnAriaLabelFlightRouteDescription() {
		let label = '';
		label +=
			this.INDRA_getTextTranslation('bookingDetails.aria.thisFlightDepartsAt') + ' ' +
			this.INDRA_printDepartureTime() +
			this.INDRA_getTextTranslation('bookingDetails.aria.from') + ' '
			this.INDRA_getDepartureLocation().name + ' ';
		label += this.INDRA_getTextTranslation('bookingDetails.aria.andArrives');
		
		const flight_arrival_other_day = this.computeArrivalOtherDay();
		if (flight_arrival_other_day > 0) {
			if (flight_arrival_other_day === 1) {
				label += this.INDRA_getTextTranslation('bookingDetails.aria.nextDay');
			} else {
				label += flight_arrival_other_day + this.INDRA_getTextTranslation('bookingDetails.aria.daysLater');
			}
		}
		
		label +=
			this.INDRA_getTextTranslation('bookingDetails.aria.at') + ' '
			this.INDRA_printArrivalTime() +
			this.INDRA_getTextTranslation('bookingDetails.aria.on') +
			this.INDRA_getArrivalLocation().name;

		return label;
	}
	
	INDRA_printDepartureTime(): String {
		let departureDate = '';
		const firstSegment = this.getFlightTripFirstSegment();
		if (firstSegment) {
			departureDate = this.INDRA_printTime(firstSegment.departureDate);
		}
		return departureDate;
	}
	
	INDRA_printArrivalTime(): String {
		let arrivalDate = '';
		const lastSegment = this.getFlightTripLastSegment();
		if (lastSegment) {
			arrivalDate = this.INDRA_printTime(lastSegment.arrivalDate);
		}
		return arrivalDate;
	}
	
	INDRA_printTime(time: string): string {
		return this.dateService.INDRA_formatDate(time, 'HH:mm');
	}
	
	private computeArrivalOtherDay() {
		let flight_arrival_other_day = 0;
		if (this.flight.isStopOver) {
			const stopOverSegments = this.flight.listSegment.filter(segment => segment.stopover);
			const lastSegment = stopOverSegments[0];
			flight_arrival_other_day = this.INDRA_printArrivalOtherDay(
				this.flight.listSegment[0].departureDate,
				lastSegment.arrivalDate
			);
		} else {
			const segmentLength = this.flight.listSegment.length;
			flight_arrival_other_day = this.INDRA_printArrivalOtherDay(
				this.flight.listSegment[0].departureDate,
				this.flight.listSegment[segmentLength - 1].arrivalDate
			);
		}
		return flight_arrival_other_day;
	}
	
	INDRA_printArrivalOtherDay(departureTime: string, returnTime: string): number {
		return Math.floor(Math.abs(this.dateService.INDRA_computeDifferenceOfDatesWithoutTimeInDays(departureTime, returnTime)));
	}
	
	private countStops() {
		const flightStops = [];
		let segmentIndex = 0;
		let lastSegmentIndex = this.flight.listSegment.length - 1;
		this.flight.listSegment.forEach(segment => {
			if (segment.stopTime > 0 && segmentIndex !== lastSegmentIndex) { flightStops.push(segment) };
			segmentIndex++;
		});
		return flightStops;
	}
	
	INDRA_printLegStops() {
		return this.countStops().length === 0 ? 'direct' : 'layover';
	}
	
	INDRA_getStops() {
		const stops = this.countStops();
		return stops.length > 0 ? stops : 0 ;
	}
	
	private getBrand() {
		return ((this.flight.options && this.flight.options.brand) ?
			this.flight.options.brand : this.bookingSearchService.INDRA_getBrandCodeFromAmadeus(this.flight.fareFamily));
	}
	
	INDRA_getBrandClass() {
		return this.getBrand().replace(/[^a-zA-Z ]/g, '');
	}
	
	INDRA_printBrand() {
		return this.getBrand();
	}
	
	INDRA_printBrandLogo() {
		return this.getBrand();
	}

	INDRA_handleDetailsClick() {
		this.sidebarService.openSidebar(['flight-details', { flight: (this.flight.assignedFlightId ? this.flight.assignedFlightId : this.flight.idFlight)}]);
		/*
		if (this.isMYB || this.isReviewFlights || this.isTimeToThinkFlights) {
			this.sidebarService.openSidebar(['flight-details', { flight: this.flight.assignedFlightId }]);
		} else {
			// for booking search flow
			this.store.dispatch(new FlightResultActions.PutSelectedFlightAction(
				Object.assign(new Flight(null, null, null), this.flight)));
				
			this.sidebarService.openSidebar(['flight-details',
				{ flight: this.flight.listSegment[0].flightNumber, mode: 'search' }]);
		}
		*/
	}
}
